import { Modal } from "antd";
import { useEffect, useRef, useState } from "react";
import "./authModal.css";

import axios, { isAxiosError } from "axios";
import { useLogin, useNotification, useRegister } from "@pankod/refine-core";
import ReCAPTCHA from "react-google-recaptcha";

type Mode = "login" | "register";
type View =
  | "login"
  | "register"
  | "forgot"
  | "forgotSuccess"
  | "forgotNotFound";

type Props = {
  open: boolean;
  onClose: () => void;
  initialMode?: Mode;
  portalUrl?: string;
  content?: Record<string, any>;
};

const API_URL = (process.env.REACT_APP_API_URL as string | undefined) ?? "";
const RECAPTCHA_SITE_KEY =
  (process.env.REACT_APP_RECAPTCHA as string | undefined) ?? "";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
const onlyDigits = (s: string) => s.replace(/\D/g, "");

const getByPath = (
  source: Record<string, any> | undefined,
  path: string,
  fallback: string,
) => {
  const value = path.split(".").reduce<any>((acc, key) => acc?.[key], source);
  return typeof value === "string" && value.length ? value : fallback;
};

export const AuthModal = ({
  open,
  onClose,
  initialMode = "login",
  portalUrl = "/portal",
  content,
}: Props) => {
  const [view, setView] = useState<View>(initialMode);
  const [captchaToken, setCaptchaToken] = useState("");
  const [isModalReady, setIsModalReady] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPass1, setShowPass1] = useState(false);
  const [showPass2, setShowPass2] = useState(false);

  const [values, setValues] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    confirm: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState("");
  const { open: notify } = useNotification();

  const { mutateAsync: loginAsync } = useLogin<{
    email: string;
    password: string;
  }>();
  const { mutateAsync: registerAsync } = useRegister<any>();

  const txt = (path: string, fallback: string) =>
    getByPath(content, path, fallback);

  useEffect(() => {
    if (!open) {
      setIsModalReady(false);
      setCaptchaToken("");
      recaptchaRef.current?.reset();
      return;
    }

    setView(initialMode);
    setLoading(false);
    setShowPass1(false);
    setShowPass2(false);
    setValues({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      password: "",
      confirm: "",
    });
    setErrors({});
    setServerError("");
    setCaptchaToken("");
    recaptchaRef.current?.reset();
  }, [open, initialMode]);

  const title =
    view === "login"
      ? txt("titles.login", "Login")
      : view === "register"
      ? txt("titles.register", "Registration")
      : txt("titles.forgot", "Password reset");

  const setField = (key: keyof typeof values, val: string) => {
    setValues((v) => ({ ...v, [key]: val }));
    setErrors((e) => ({ ...e, [key]: "" }));
    setServerError("");
  };

  const extractErrorMessage = (err: any): string => {
    if (!err) return txt("messages.genericError", "Something went wrong");

    if (typeof err === "object" && (err.name || err.message)) {
      const n = typeof err.name === "string" ? err.name : "";
      const m = typeof err.message === "string" ? err.message : "";
      return (
        (n && n !== "Error" ? n : "") ||
        m ||
        txt("messages.genericError", "Something went wrong")
      );
    }

    if (typeof err === "string") return err;

    if (isAxiosError(err)) {
      const data: any = err.response?.data;
      if (data?.errors?.length) {
        return data.errors
          .map((x: any) => x?.msg)
          .filter(Boolean)
          .join(";\n");
      }
      if (data?.message) return String(data.message);
      return err.message || txt("messages.genericError", "Something went wrong");
    }

    return String(err?.message || err);
  };

  const validate = (): boolean => {
    const e: Record<string, string> = {};

    const email = values.email.trim();
    const phoneDigits = onlyDigits(values.phone);

    if (!email) e.email = txt("errors.emailRequired", "Enter email");
    else if (!emailRegex.test(email))
      e.email = txt("errors.emailInvalid", "Invalid email");

    if (view === "login") {
      if (!values.password)
        e.password = txt("errors.passwordRequired", "Enter password");
    }

    if (view === "register") {
      if (!values.firstName.trim())
        e.firstName = txt("errors.firstNameRequired", "Enter first name");
      if (!values.lastName.trim())
        e.lastName = txt("errors.lastNameRequired", "Enter last name");

      if (!values.phone.trim()) {
        e.phone = txt("errors.phoneRequired", "Enter phone number");
      } else if (phoneDigits.length !== 12) {
        e.phone = txt("errors.phoneInvalid", "Phone number is invalid");
      }

      if (!values.password) {
        e.password = txt("errors.passwordRequired", "Enter password");
      } else if (values.password.length < 6) {
        e.password = txt("errors.passwordMin", "Minimum 6 characters");
      }

      if (!values.confirm) {
        e.confirm = txt("errors.confirmRequired", "Confirm password");
      } else if (values.confirm !== values.password) {
        e.confirm = txt("errors.confirmMismatch", "Passwords do not match");
      }

      if (!captchaToken) {
        e.captcha = txt("errors.captchaRequired", "Confirm you are not a robot");
      }
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) {
      notify?.({
        type: "error",
        message: txt("messages.formInvalidTitle", "Fill required fields"),
        description: txt("messages.formInvalidDescription", "Check form values"),
      });
      return;
    }

    setLoading(true);
    setServerError("");

    try {
      if (view === "login") {
        await loginAsync({
          email: values.email.trim(),
          password: values.password,
        });
        onClose();
        window.location.href = portalUrl;
        return;
      }

      if (view === "register") {
        await registerAsync({
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          name: values.firstName.trim(),
          email: values.email.trim(),
          phone: values.phone.trim(),
          password: values.password,
          captchaToken,
        });

        notify?.({
          type: "success",
          message: txt("messages.registerSuccessTitle", "Registration successful"),
          description: txt(
            "messages.registerSuccessDescription",
            "You can now sign in",
          ),
        });

        setCaptchaToken("");
        recaptchaRef.current?.reset();
        setView("login");
        setValues((v) => ({ ...v, password: "", confirm: "" }));
        return;
      }

      if (view === "forgot") {
        if (!API_URL) {
          throw new Error(
            txt("messages.apiMissing", "API URL is not configured (REACT_APP_API_URL)"),
          );
        }

        try {
          const base = API_URL.endsWith("/") ? API_URL : `${API_URL}/`;
          await axios.post(`${base}partner/forgot_password`, {
            email: values.email.trim(),
          });
          setView("forgotSuccess");
        } catch (err: any) {
          const msg = extractErrorMessage(err).toLowerCase();
          if (
            msg.includes("not found") ||
            msg.includes("не знайден") ||
            msg.includes("користувач")
          ) {
            setView("forgotNotFound");
          } else {
            setServerError(extractErrorMessage(err));
          }
        }
        return;
      }
    } catch (err: any) {
      const msg = extractErrorMessage(err);
      setServerError(msg);
      notify?.({
        type: "error",
        message: txt("messages.errorTitle", "Error"),
        description: msg,
      });
    } finally {
      setLoading(false);
    }
  };

  const go = (next: View) => {
    setView(next);
    setErrors({});
    setServerError("");
    setShowPass1(false);
    setShowPass2(false);
    setCaptchaToken("");
    recaptchaRef.current?.reset();
    setValues((v) => ({ ...v, password: "", confirm: "" }));
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      destroyOnClose
      className="oc-auth"
      afterOpenChange={(opened) => {
        setIsModalReady(opened);
      }}
      width={520}
      closeIcon={<span className="oc-auth__closeIcon" />}
    >
      <div className="oc-auth__title">{title}</div>

      {view === "forgotSuccess" && (
        <div className="oc-auth__state">
          <div className="oc-auth__stateIconWrap">
            <span
              className="oc-auth__stateIcon"
              style={{
                WebkitMaskImage: `url(/assets/icons/check.svg)`,
                maskImage: `url(/assets/icons/check.svg)`,
              }}
            />
          </div>

          <div className="oc-auth__stateText">
            {txt(
              "messages.forgotSuccessText",
              "Password reset email has been sent.",
            )}
          </div>

          <button
            className="oc-auth__btn oc-auth__btn--primary"
            type="button"
            onClick={() => go("login")}
          >
            {txt("buttons.backToLogin", "Back to login")}
          </button>
        </div>
      )}

      {view === "forgotNotFound" && (
        <div className="oc-auth__state">
          <div className="oc-auth__stateText">
            {txt("messages.forgotNotFoundText", "User with this email was not found")}
          </div>

          <button
            className="oc-auth__btn oc-auth__btn--primary"
            type="button"
            onClick={() => go("forgot")}
          >
            {txt("buttons.tryAnotherEmail", "Try another email")}
          </button>
        </div>
      )}

      {(view === "login" || view === "register" || view === "forgot") && (
        <div className="oc-auth__form">
          {serverError && <div className="oc-auth__serverErr">{serverError}</div>}

          {view === "register" && (
            <>
              <div className="oc-auth__field">
                <div className="oc-auth__label">
                  {txt("labels.firstName", "First name")}{" "}
                  <span className="oc-auth__req">*</span>
                </div>
                <input
                  className={`oc-auth__input ${errors.firstName ? "is-err" : ""}`}
                  placeholder={txt("placeholders.firstName", "Enter first name")}
                  value={values.firstName}
                  onChange={(e) => setField("firstName", e.target.value)}
                />
                {!!errors.firstName && <div className="oc-auth__err">{errors.firstName}</div>}
              </div>

              <div className="oc-auth__field">
                <div className="oc-auth__label">
                  {txt("labels.lastName", "Last name")}{" "}
                  <span className="oc-auth__req">*</span>
                </div>
                <input
                  className={`oc-auth__input ${errors.lastName ? "is-err" : ""}`}
                  placeholder={txt("placeholders.lastName", "Enter last name")}
                  value={values.lastName}
                  onChange={(e) => setField("lastName", e.target.value)}
                />
                {!!errors.lastName && <div className="oc-auth__err">{errors.lastName}</div>}
              </div>
            </>
          )}

          <div className="oc-auth__field">
            <div className="oc-auth__label">
              {txt("labels.email", "Email")} <span className="oc-auth__req">*</span>
            </div>
            <input
              className={`oc-auth__input ${errors.email ? "is-err" : ""}`}
              placeholder={txt("placeholders.email", "Enter email")}
              value={values.email}
              onChange={(e) => setField("email", e.target.value)}
              inputMode="email"
            />
            {!!errors.email && <div className="oc-auth__err">{errors.email}</div>}
          </div>

          {view === "register" && (
            <div className="oc-auth__field">
              <div className="oc-auth__label">
                {txt("labels.phone", "Phone")} <span className="oc-auth__req">*</span>
              </div>
              <input
                className={`oc-auth__input ${errors.phone ? "is-err" : ""}`}
                placeholder={txt("placeholders.phone", "+38 0__ ___ __ __")}
                value={values.phone}
                onChange={(e) => setField("phone", e.target.value)}
                inputMode="tel"
              />
              {!!errors.phone && <div className="oc-auth__err">{errors.phone}</div>}
            </div>
          )}

          {view !== "forgot" && (
            <div className="oc-auth__field">
              <div className="oc-auth__label">
                {txt("labels.password", "Password")}{" "}
                <span className="oc-auth__req">*</span>
              </div>

              <div
                className={`oc-auth__passWrap ${
                  errors.password ? "is-err" : ""
                }`}
              >
                <input
                  className="oc-auth__input oc-auth__input--pass"
                  placeholder={txt("placeholders.password", "Enter password")}
                  type={showPass1 ? "text" : "password"}
                  value={values.password}
                  onChange={(e) => setField("password", e.target.value)}
                />

                <button
                  className="oc-auth__passBtn"
                  type="button"
                  onClick={() => setShowPass1((s) => !s)}
                  aria-label={txt("aria.togglePassword", "Toggle password visibility")}
                >
                  <span
                    className="oc-auth__passIcon"
                    style={{
                      WebkitMaskImage: `url(${
                        showPass1
                          ? "/assets/icons/eye-off.svg"
                          : "/assets/icons/eye.svg"
                      })`,
                      maskImage: `url(${
                        showPass1
                          ? "/assets/icons/eye-off.svg"
                          : "/assets/icons/eye.svg"
                      })`,
                    }}
                  />
                </button>
              </div>

              {!!errors.password && <div className="oc-auth__err">{errors.password}</div>}
            </div>
          )}

          {view === "register" && (
            <div className="oc-auth__field">
              <div className="oc-auth__label">
                {txt("labels.confirmPassword", "Confirm password")}{" "}
                <span className="oc-auth__req">*</span>
              </div>

              <div
                className={`oc-auth__passWrap ${errors.confirm ? "is-err" : ""}`}
              >
                <input
                  className="oc-auth__input oc-auth__input--pass"
                  placeholder={txt("placeholders.confirmPassword", "Repeat password")}
                  type={showPass2 ? "text" : "password"}
                  value={values.confirm}
                  onChange={(e) => setField("confirm", e.target.value)}
                />

                <button
                  className="oc-auth__passBtn"
                  type="button"
                  onClick={() => setShowPass2((s) => !s)}
                  aria-label={txt(
                    "aria.toggleConfirmPassword",
                    "Toggle confirm password visibility",
                  )}
                >
                  <span
                    className="oc-auth__passIcon"
                    style={{
                      WebkitMaskImage: `url(${
                        showPass2
                          ? "/assets/icons/eye-off.svg"
                          : "/assets/icons/eye.svg"
                      })`,
                      maskImage: `url(${
                        showPass2
                          ? "/assets/icons/eye-off.svg"
                          : "/assets/icons/eye.svg"
                      })`,
                    }}
                  />
                </button>
              </div>

              {!!errors.confirm && <div className="oc-auth__err">{errors.confirm}</div>}
            </div>
          )}

          {view === "register" && isModalReady && (
            <div className="oc-auth__field">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={(token) => {
                  setCaptchaToken(token || "");
                  setErrors((prev) => ({ ...prev, captcha: "" }));
                  setServerError("");
                }}
                onExpired={() => {
                  setCaptchaToken("");
                }}
              />

              {!!errors.captcha && <div className="oc-auth__err">{errors.captcha}</div>}
            </div>
          )}

          {view === "login" && (
            <button
              className="oc-auth__link"
              type="button"
              onClick={() => go("forgot")}
            >
              {txt("buttons.forgotPassword", "Forgot password?")}
            </button>
          )}

          <button
            className="oc-auth__btn oc-auth__btn--primary"
            type="button"
            onClick={submit}
            disabled={loading}
          >
            {loading
              ? "..."
              : view === "login"
              ? txt("buttons.login", "Login")
              : view === "register"
              ? txt("buttons.register", "Register")
              : txt("buttons.sendReset", "Send")}
          </button>

          {view === "login" && (
            <button
              className="oc-auth__btn oc-auth__btn--secondary"
              type="button"
              onClick={() => go("register")}
              disabled={loading}
            >
              {txt("buttons.goToRegister", "Create account")}
            </button>
          )}

          {view === "register" && (
            <button
              className="oc-auth__btn oc-auth__btn--secondary"
              type="button"
              onClick={() => go("login")}
              disabled={loading}
            >
              {txt("buttons.haveAccount", "I already have an account")}
            </button>
          )}

          {view === "forgot" && (
            <button
              className="oc-auth__btn oc-auth__btn--secondary"
              type="button"
              onClick={() => go("login")}
              disabled={loading}
            >
              {txt("buttons.backToLogin", "Back to login")}
            </button>
          )}
        </div>
      )}
    </Modal>
  );
};
